import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { clientEnv } from "../../env";
import type { Database } from "./database.types";

export type PortalSupabaseClient = SupabaseClient<Database>;

let browserClient: PortalSupabaseClient | null = null;

/**
 * Adds a fresh `X-Correlation-Id` to every request the client makes.
 *
 * The other half of migration 27. `private.write_audit_event` reads `x-correlation-id` out of the
 * headers PostgREST puts on the connection, and a read with nobody sending is a column that stays
 * null. A browser calling PostgREST directly is the one path with no server in front of it to mint
 * the value, so it is minted here.
 *
 * One ID per request rather than one per session. The column exists to put the rows of a single
 * request beside each other; a value that lasted a whole session would group everything a person did
 * all day and answer nothing.
 *
 * The value is client-chosen and the database treats it that way: it is cast to `uuid` and anything
 * else becomes null, and schema.md records that the column is a join key rather than evidence. What
 * identifies the row is `actor_user_id` and `occurred_at`, and the database derives both.
 */
function correlatedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
  headers.set("X-Correlation-Id", crypto.randomUUID());

  return fetch(input, { ...init, headers });
}

/**
 * The one Supabase client the browser gets.
 *
 * architecture.md section 4: the official client owns token persistence and refresh, and the
 * application never copies a refresh token anywhere of its own. It holds only the project URL and
 * the publishable key — the secret key never reaches the browser and neither does any Cloudflare
 * credential.
 *
 * A single instance matters beyond tidiness: two clients would each install their own auth state
 * listener and refresh timer against the same stored session, and the losing one would keep
 * serving a token the other had already rotated.
 */
export function getSupabaseBrowserClient(): PortalSupabaseClient {
  if (browserClient) {
    return browserClient;
  }

  if (!clientEnv.VITE_SUPABASE_URL || !clientEnv.VITE_SUPABASE_PUBLISHABLE_KEY) {
    throw new Error("Supabase public environment is not configured.");
  }

  browserClient = createClient<Database>(
    clientEnv.VITE_SUPABASE_URL,
    clientEnv.VITE_SUPABASE_PUBLISHABLE_KEY,
    {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        flowType: "pkce"
      },
      realtime: {
        // Private channels only. A public channel would authorise nothing at all.
        params: { eventsPerSecond: 5 }
      },
      global: { fetch: correlatedFetch }
    }
  );

  return browserClient;
}

/** Test seam. Production code never calls this. */
export function resetSupabaseBrowserClientForTests(): void {
  browserClient = null;
}
